import React from 'react';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import apiService from '../apiService';
import type { ReportingPreviewResult } from '../apiService';

type PreviewFailure = Extract<ReportingPreviewResult, { success: false }>['reason'];

// UX Standard S-03: a failed load renders as an error with a retry. A 403 is
// the module's answer (the `reporting` module is off for this organization, or
// was switched on less than a minute ago); anything else is transient, so the
// copy must not blame the entitlement for it.
const FAILURE_COPY: Record<PreviewFailure, string> = {
  not_enabled: 'Reporting is not enabled for your organization.',
  unavailable: 'The Reporting preview could not be loaded. Try again.',
};

export default function ReportingPreviewRoute() {
  const { authToken } = useAuth();
  const [html, setHtml] = React.useState<string | null>(null);
  const [failure, setFailure] = React.useState<PreviewFailure | null>(null);
  const [attempt, setAttempt] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    setHtml(null);
    setFailure(null);
    if (!authToken) return;
    apiService.getReportingPreview(authToken).then(result => {
      if (cancelled) return;
      if (result.success) setHtml(result.html);
      else setFailure(result.reason);
    }).catch(() => {
      if (!cancelled) setFailure('unavailable');
    });
    return () => { cancelled = true; };
  }, [authToken, attempt]);

  return (
    <div className="h-full bg-bg flex flex-col">
      <div className="border-b border-border-default px-6 py-4">
        <h1 className="text-lg font-semibold text-fg">Reporting preview</h1>
        <p className="text-sm text-fg-secondary">Internal preview · example data only</p>
      </div>
      {failure ? (
        <div role="alert" className="m-6 px-3 py-2 bg-danger-bg border border-danger rounded-md flex items-center justify-between gap-3">
          <p className="text-sm text-danger">{FAILURE_COPY[failure]}</p>
          <button
            type="button"
            onClick={() => setAttempt(n => n + 1)}
            className="text-sm text-accent hover:text-accent-hover underline"
          >
            Retry
          </button>
        </div>
      ) : html ? (
        <iframe title="BARK Reporting internal preview" srcDoc={html} sandbox="allow-scripts" referrerPolicy="no-referrer" className="flex-1 w-full border-0" />
      ) : (
        <p role="status" className="p-6 flex items-center gap-2 text-sm text-fg-secondary">
          <Loader2 size={16} strokeWidth={1.5} className="animate-spin" aria-hidden="true" />
          Loading preview…
        </p>
      )}
    </div>
  );
}
